import * as React from "react"

import { cn } from "@/lib/utils"
import MarkdownEditor from "./MarkdownEditor"

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  markdown?: boolean; // Enable/disable markdown mode (default: true)
  preview?: 'edit' | 'live' | 'preview'; // Preview mode for markdown editor
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, markdown = true, preview = 'live', value, onChange, ...props }, ref) => {
    const handleMarkdownChange = React.useCallback((val: string | undefined) => {
      if (onChange) {
        // Create a synthetic event for compatibility
        const syntheticEvent = {
          target: { value: val || '' },
          currentTarget: { value: val || '' },
        } as React.ChangeEvent<HTMLTextAreaElement>;
        onChange(syntheticEvent);
      }
    }, [onChange]);

    if (markdown) {
      return (
        <MarkdownEditor
          value={value || ''}
          onChange={handleMarkdownChange}
          preview={preview}
          placeholder={props.placeholder}
          disabled={props.disabled}
          className={className}
          height={props.rows ? props.rows * 20 : 200}
        />
      );
    }

    return (
      <textarea
        className={cn(
          "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        ref={ref}
        value={value}
        onChange={onChange}
        {...props}
      />
    )
  }
)
Textarea.displayName = "Textarea"

export { Textarea }
